import React, { createContext, useContext, useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  AI_ZIP_KIND,
  buildResume,
  clearSavedTray,
  getTrayStorageKey,
  INTERRUPTED_ERROR_TYPE,
  INTERRUPTED_STATUS,
  isInterruptedUpload,
  loadTray,
  matchPickedFiles,
  retryContextFor,
  saveTray,
} from '../utils/uploadTrayStorage';

const UploadNotificationContext = createContext();

// How often a busy upload batch writes the tray to browser storage. Finished
// and failed files are what matter after a crash, and they change far less
// often than progress ticks, so a one-second window loses at most a second.
const TRAY_SAVE_THROTTLE_MS = 1000;

export const useUploadNotification = () => {
  const context = useContext(UploadNotificationContext);
  if (!context) {
    throw new Error('useUploadNotification must be used within an UploadNotificationProvider');
  }
  return context;
};

export const UploadNotificationProvider = ({ children }) => {
  // Whatever the previous page load left behind (a reload or crash mid-upload).
  // Read once, before the first render, so the tray never flashes empty.
  const restoredTrayRef = useRef(undefined);
  if (restoredTrayRef.current === undefined) {
    restoredTrayRef.current = loadTray();
  }
  const [uploads, setUploads] = useState(() => restoredTrayRef.current?.uploads ?? []);
  const [failedUploads, setFailedUploads] = useState(() => restoredTrayRef.current?.failedUploads ?? []);
  // The restored tray is shown once the user is confirmed signed in (below),
  // not on a login screen.
  const [showNotification, setShowNotification] = useState(false);
  const [uploadAbortControllers, setUploadAbortControllers] = useState(new Map());
  // Persistent storage for upload information - survives state changes
  const [uploadInfoMap, setUploadInfoMap] = useState(new Map());
  // Ref to track completed upload IDs - used when user cancels after upload finished
  const completedUploadIdsRef = useRef(new Set());
  // Ref to track cancelled upload IDs - used when user cancels BEFORE upload starts
  const cancelledUploadIdsRef = useRef(new Set());
  
  // Optimization state tracking
  const [uploadedDocIds, setUploadedDocIds] = useState([]);
  const [optimizedDocIds, setOptimizedDocIds] = useState([]);
  const [isUploadComplete, setIsUploadComplete] = useState(false);

  // Get authentication state from Redux
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const dispatch = useDispatch();
  
  // Sync optimization state from uploadUtils to React state
  React.useEffect(() => {
    // Poll optimization state from uploadUtils to keep React state in sync
    const interval = setInterval(() => {
      try {
        const { getUploadedDocIds, getOptimizedDocIds, getIsUploadComplete } = require('../utils/uploadUtils');
        const currentUploaded = getUploadedDocIds();
        const currentOptimized = getOptimizedDocIds();
        const currentIsComplete = getIsUploadComplete();
        
        setUploadedDocIds(prev => {
          const prevStr = JSON.stringify(prev.sort());
          const currentStr = JSON.stringify([...currentUploaded].sort());
          if (prevStr !== currentStr) {
            console.log(`[UploadContext] Uploaded IDs updated: ${prev.length} -> ${currentUploaded.length}`);
            return [...currentUploaded];
          }
          return prev;
        });
        
        setOptimizedDocIds(prev => {
          const prevStr = JSON.stringify(prev.sort());
          const currentStr = JSON.stringify([...currentOptimized].sort());
          if (prevStr !== currentStr) {
            console.log(`[UploadContext] Optimized IDs updated: ${prev.length} -> ${currentOptimized.length}`);
            return [...currentOptimized];
          }
          return prev;
        });
        
        setIsUploadComplete(prev => {
          if (prev !== currentIsComplete) {
            console.log(`[UploadContext] Upload complete status updated: ${prev} -> ${currentIsComplete}`);
            return currentIsComplete;
          }
          return prev;
        });
      } catch (error) {
        console.error('[UploadContext] Error syncing optimization state:', error);
      }
    }, 100); // Poll every 100ms for very fast updates to prevent empty status
    
    return () => clearInterval(interval);
  }, []);



  const addUpload = (upload) => {
    // Store upload info in persistent map
    const uploadInfo = {
      fileName: upload.fileName,
      fileSize: upload.fileSize,
      type: upload.type,
      status: upload.status,
      uploadIndex: upload.uploadIndex,
      totalUploads: upload.totalUploads,
      batchId: upload.batchId,
      matterId: upload.matterId,
      matterLabel: upload.matterLabel
    };
    
    setUploadInfoMap(prev => {
      const newMap = new Map(prev);
      newMap.set(upload.id, uploadInfo);
      return newMap;
    });
    
    setUploads(prev => [...prev, upload]);
    setShowNotification(true);
  };

  const addUploadAbortController = (uploadId, abortController) => {
    setUploadAbortControllers(prev => new Map(prev).set(uploadId, abortController));
  };

  const updateUpload = (uploadId, updates) => {
    setUploads(prev => {
      const updated = prev.map(upload => 
        upload.id === uploadId ? { ...upload, ...updates } : upload
      );
      return updated;
    });
  };

  const removeUpload = (uploadId) => {
    setUploads(prev => prev.filter(upload => upload.id !== uploadId));
    
    // Keep the upload info in the map for failed uploads
  };

  const handleCancelledUpload = (uploadId, uploadInfo) => {
    // If upload already completed (file is on server), keep it in uploads as completed 
    if (completedUploadIdsRef.current.has(uploadId)) {
      setUploads(prev => prev.map(upload =>
        upload.id === uploadId ? { ...upload, progress: 100, status: 'completed' } : upload
      ));
      return;
    }
    const cancelledUpload = {
      id: uploadId,
      fileName: uploadInfo.fileName,
      fileSize: uploadInfo.fileSize,
      type: uploadInfo.type,
      status: 'cancelled',
      error: 'Upload cancelled by user',
      uploadIndex: uploadInfo.uploadIndex,
      totalUploads: uploadInfo.totalUploads,
      batchId: uploadInfo.batchId,
      matterId: uploadInfo.matterId,
      matterLabel: uploadInfo.matterLabel
    };
    
    setFailedUploads(prevFailed => [...prevFailed, cancelledUpload]);
    setUploads(prev => prev.filter(upload => upload.id !== uploadId));
  };

  const cancelUpload = (uploadId) => {
    // Track cancellation so processFiles skips this upload when signed URL API returns
    cancelledUploadIdsRef.current.add(uploadId);
    // Abort the specific API request (if controller exists)
    const abortController = uploadAbortControllers.get(uploadId);
    if (abortController) {
      abortController.abort();
      setUploadAbortControllers(prev => {
        const newMap = new Map(prev);
        newMap.delete(uploadId);
        return newMap;
      });
    }
    
    // Get upload info from persistent map and handle cancellation
    const uploadInfo = uploadInfoMap.get(uploadId);
    if (uploadInfo) {
      handleCancelledUpload(uploadId, uploadInfo);
    }
  };

  const createCompleteFailedUpload = (failedUpload, uploadInfo) => {
    return {
      id: failedUpload.id,
      fileName: uploadInfo?.fileName || failedUpload.fileName || 'Unknown file',
      fileSize: uploadInfo?.fileSize ?? failedUpload.fileSize ?? 0,
      type: failedUpload.type ?? uploadInfo?.type,
      status: failedUpload.status || 'failed',
      error: failedUpload.error || 'Upload failed',
      uploadIndex: failedUpload.uploadIndex || uploadInfo?.uploadIndex,
      totalUploads: failedUpload.totalUploads || uploadInfo?.totalUploads,
      batchId: uploadInfo?.batchId || failedUpload.batchId,
      matterId: failedUpload.matterId || uploadInfo?.matterId,
      matterLabel: failedUpload.matterLabel || uploadInfo?.matterLabel
    };
  };

  const addFailedUpload = (failedUpload) => {
    // Check if this upload is already in the failed list to prevent duplicates
    setFailedUploads(prev => {
      const alreadyExists = prev.some(upload => upload.id === failedUpload.id);
      if (alreadyExists) {
        return prev; // Don't add duplicate
      }
      
      // If failedUpload already has fileName and fileSize, use it directly —
      // but still fill in which client it was for if the caller left that out.
      if (failedUpload.fileName && failedUpload.fileSize) {
        if (failedUpload.matterLabel) return [...prev, failedUpload];
        const knownInfo = uploadInfoMap.get(failedUpload.id);
        return [...prev, {
          ...failedUpload,
          matterId: failedUpload.matterId || knownInfo?.matterId,
          matterLabel: knownInfo?.matterLabel,
        }];
      }
      
      // Otherwise, try to get from uploadInfoMap and create complete failed upload
      const uploadInfo = uploadInfoMap.get(failedUpload.id);
      const completeFailedUpload = createCompleteFailedUpload(failedUpload, uploadInfo);
      
      return [...prev, completeFailedUpload];
    });
    
    setShowNotification(true);
  };

  const removeFailedUpload = (uploadId) => {
    setFailedUploads(prev => prev.filter(upload => upload.id !== uploadId));
  };

  // Resend one failed file from the bytes + destination we still hold (see
  // uploadUtils.js's `retryContext`, attached to every failure that is not
  // the file's own fault). The file goes back into the SAME folder of the
  // SAME upload session — the server treats a matching session id as a
  // continuation of that folder upload, not a conflicting new one — and
  // keeps the session's AI-processing marker, so a retried claim-file
  // document gets the same treatment its siblings did.
  const resendFailedUpload = async (failedUpload, fileToSend, displayName) => {
    const retryContext = failedUpload?.retryContext;
    removeFailedUpload(failedUpload.id);

    const newUploadId = `upload-${Date.now()}-${crypto.randomUUID()}`;
    addUpload({
      id: newUploadId,
      fileName: displayName,
      fileSize: fileToSend.size,
      type: failedUpload.type,
      status: 'uploading',
      progress: 0,
      batchId: retryContext.batchId,
      matterId: failedUpload.matterId,
      matterLabel: failedUpload.matterLabel,
      // Keep its destination while it re-sends: if the page reloads again
      // mid-resend, this row must still be finishable, not a dead end.
      resume: buildResume(retryContext),
    });

    try {
      const { uploadSingleFileWithSignedUrl, runPagination } = await import('../utils/uploadUtils');
      const response = await uploadSingleFileWithSignedUrl({
        file: fileToSend,
        matterDetails: retryContext.matterDetails,
        uniqueId: retryContext.uniqueId,
        newly_created: retryContext.newly_created,
        relativePath: retryContext.relativePath,
        overrideExisting: retryContext.overrideExisting,
        batchId: retryContext.batchId,
        aiProcess: retryContext.aiProcess,
        onUploadProgress: (progressEvent) => {
          const loaded = progressEvent.loaded || 0;
          const total = progressEvent.total || fileToSend.size;
          if (total) {
            updateUpload(newUploadId, { progress: Math.round((loaded * 100) / total) });
          }
        },
      });

      markUploadCompleted(newUploadId);
      updateUpload(newUploadId, { progress: 100, status: 'completed' });
      // Without this, the file lands on the server but the folder view the
      // user is looking at stays stale until they manually refresh (Bugbot
      // finding on #687, PR fix/rename-retry-folder-target) — every other
      // successful-upload path refreshes the current matter/folder listing
      // the same way.
      runPagination({
        dispatch,
        matterDetails: retryContext.matterDetails,
        uniqueId: retryContext.uniqueId,
      });
      return { success: true, documentId: response?.documentId };
    } catch (error) {
      removeUpload(newUploadId);
      const { extractErrorMessage } = await import('../utils/uploadUtils');
      const errorType = error?.errorType ?? null;
      addFailedUpload({
        id: newUploadId,
        fileName: displayName,
        fileSize: fileToSend.size,
        type: failedUpload.type,
        status: 'failed',
        matterId: failedUpload.matterId,
        matterLabel: failedUpload.matterLabel,
        error: extractErrorMessage(error),
        errorType,
        // Failed again — keep the (possibly renamed) bytes so the user can
        // try once more from here rather than dead-ending.
        retryContext: { ...retryContext, file: fileToSend },
      });
      return { success: false, error: extractErrorMessage(error) };
    }
  };

  // One-click retry for a failed upload: same file, same name, same place.
  const retryUpload = async (failedUpload) => {
    const retryContext = failedUpload?.retryContext;
    if (!retryContext?.file) {
      return { success: false, error: 'This file has to be picked again to retry.' };
    }
    return resendFailedUpload(failedUpload, retryContext.file, failedUpload.fileName);
  };

  // Retry every failed upload we can, a few at a time — a 20-file tail of a
  // claim-file drop that hit a network blip is the common case, and the
  // user should not have to click twenty times or re-pick twenty files.
  const RETRY_ALL_CONCURRENCY = 4;
  const retryAllFailedUploads = async (candidates) => {
    const { isRetryableFailedUpload } = await import('../utils/uploadUtils');
    const queue = (candidates ?? failedUploads).filter(isRetryableFailedUpload);
    const results = [];
    let next = 0;
    const worker = async () => {
      while (next < queue.length) {
        const item = queue[next++];
        results.push(await retryUpload(item));
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(RETRY_ALL_CONCURRENCY, queue.length) }, worker)
    );
    return {
      attempted: queue.length,
      succeeded: results.filter((r) => r.success).length,
      failed: results.filter((r) => !r.success).length,
    };
  };

  // Duplicate-filename rejections are the one failure the user can fix
  // themselves without re-picking the file from their computer: re-upload
  // the bytes under the name the user chose.
  const renameAndRetryUpload = async (failedUpload, newFileName) => {
    const retryContext = failedUpload?.retryContext;
    const trimmedName = (newFileName || '').trim();
    if (!retryContext?.file || !trimmedName) {
      return { success: false, error: 'Enter a new file name to retry.' };
    }
    const renamedFile = new File([retryContext.file], trimmedName, {
      type: retryContext.file.type,
    });
    return resendFailedUpload(failedUpload, renamedFile, trimmedName);
  };

  // After a reload the files themselves are gone (a browser never lets a page
  // re-read a file from disk on its own), but the tray still knows which
  // files were cut off and where each was headed. The user picks their files
  // again — the whole folder is fine — and only those that match an
  // interrupted entry are sent, each back to its own matter and folder.
  // How a server-unpacked zip ended, shown in the tray and refreshed in the
  // matter the user is looking at.
  const settleZipJob = async (uploadId, base, finalJob) => {
    const { summarizeAiZipJob } = await import('../service/aiZipUploadService');
    const summary = summarizeAiZipJob(finalJob);
    if (finalJob.status === 'failed' || finalJob.status === 'aborted') {
      removeUpload(uploadId);
      addFailedUpload({
        id: uploadId,
        fileName: base.matchName,
        fileSize: base.fileSize,
        type: AI_ZIP_KIND,
        status: 'failed',
        matterId: base.matterId,
        matterLabel: base.matterLabel,
        error: summary,
      });
      return { success: false, error: summary };
    }
    markUploadCompleted(uploadId);
    updateUpload(uploadId, {
      progress: 100,
      status: 'completed',
      fileName: `${base.matchName} — ${summary}`,
    });
    const { runPagination } = await import('../utils/uploadUtils');
    runPagination({
      dispatch,
      matterDetails: base.resume?.matterDetails,
      uniqueId: base.resume?.folderId ? `F${base.resume.folderId}` : null,
    });
    return { success: true };
  };

  // Sends a re-picked zip again, then follows the server's unpacking.
  const resendInterruptedZip = async (entry, file) => {
    removeFailedUpload(entry.id);
    const epoch = signOutEpochRef.current;
    const isStale = () => epoch !== signOutEpochRef.current;
    let jobCreated = false;
    const newUploadId = `ai-zip-${Date.now()}-${crypto.randomUUID()}`;
    const abortController = new AbortController();
    const base = {
      matchName: file.name,
      fileSize: file.size,
      matterId: entry.matterId,
      matterLabel: entry.matterLabel,
      resume: entry.resume,
    };
    addUpload({
      id: newUploadId,
      fileName: file.name,
      matchName: file.name,
      fileSize: file.size,
      progress: 0,
      status: 'uploading',
      type: AI_ZIP_KIND,
      matterId: entry.matterId,
      matterLabel: entry.matterLabel,
      resume: entry.resume,
    });
    addUploadAbortController(newUploadId, abortController);
    try {
      const { uploadZipServerSide, pollAiZipJob } = await import('../service/aiZipUploadService');
      const job = await uploadZipServerSide(file, {
        matterId: entry.resume.matterId,
        folderId: entry.resume.folderId,
        signal: abortController.signal,
        onProgress: (percent) => updateUpload(newUploadId, { progress: percent }),
      });
      jobCreated = true;
      if (isStale()) return { success: false, error: 'Signed out' };
      updateUpload(newUploadId, {
        progress: 100,
        serverJobId: job.job_id,
        fileName: `${file.name} — extracting…`,
      });
      const finalJob = await pollAiZipJob(job.job_id, {
        onUpdate: (j) => {
          if (!isStale() && j.total_pdfs > 0) {
            updateUpload(newUploadId, {
              fileName: `${file.name} — extracting ${j.extracted_count}/${j.total_pdfs}`,
            });
          }
        },
      });
      if (isStale()) return { success: false, error: 'Signed out' };
      return await settleZipJob(newUploadId, base, finalJob);
    } catch (error) {
      if (isStale()) return { success: false, error: 'Signed out' };
      removeUpload(newUploadId);
      if (jobCreated) {
        // Every byte arrived and the server owns the unpacking; only the
        // status check failed. Asking for the zip again would send and unpack
        // the same claim file twice, so do not offer that.
        const message = 'Sent, but could not confirm it finished unpacking. Look in the matter for its files before sending it again.';
        addFailedUpload({
          id: newUploadId,
          fileName: file.name,
          fileSize: file.size,
          type: AI_ZIP_KIND,
          status: 'failed',
          matterId: entry.matterId,
          matterLabel: entry.matterLabel,
          error: message,
        });
        return { success: false, error: message };
      }
      const message = error?.response?.data?.error || error?.message || 'Sending failed';
      // Still resumable: keep its destination and ask for the file again.
      addFailedUpload({
        id: newUploadId,
        fileName: file.name,
        matchName: file.name,
        fileSize: file.size,
        type: AI_ZIP_KIND,
        status: INTERRUPTED_STATUS,
        errorType: INTERRUPTED_ERROR_TYPE,
        matterId: entry.matterId,
        matterLabel: entry.matterLabel,
        resume: entry.resume,
        error: `${message}. Pick the file again to try once more.`,
      });
      return { success: false, error: message };
    }
  };

  // A zip that finished arriving before the reload is still being unpacked
  // by the server. Check on it instead of treating it as cut off.
  const watchedJobsRef = useRef(new Set());
  const watchRestoredZipJob = async (upload) => {
    const epoch = signOutEpochRef.current;
    const isStale = () => epoch !== signOutEpochRef.current;
    try {
      const { pollAiZipJob } = await import('../service/aiZipUploadService');
      const finalJob = await pollAiZipJob(upload.serverJobId, {
        onUpdate: (j) => {
          if (!isStale() && j.total_pdfs > 0) {
            updateUpload(upload.id, {
              fileName: `${upload.matchName || upload.fileName} — extracting ${j.extracted_count}/${j.total_pdfs}`,
            });
          }
        },
      });
      if (isStale()) return;
      await settleZipJob(upload.id, { ...upload, matchName: upload.matchName || upload.fileName }, finalJob);
    } catch (error) {
      if (isStale()) return;
      removeUpload(upload.id);
      addFailedUpload({
        id: upload.id,
        fileName: upload.matchName || upload.fileName,
        fileSize: upload.fileSize,
        type: AI_ZIP_KIND,
        status: 'failed',
        matterId: upload.matterId,
        matterLabel: upload.matterLabel,
        error: 'Could not check whether this zip finished unpacking. Look in the matter for its files.',
      });
    }
  };
  useEffect(() => {
    if (!isAuthenticated) return;
    for (const upload of uploads) {
      if (!upload.restored || !upload.serverJobId || watchedJobsRef.current.has(upload.id)) continue;
      watchedJobsRef.current.add(upload.id);
      watchRestoredZipJob(upload);
    }
  }, [isAuthenticated, uploads]);

  const resumeInterruptedUploads = async (pickedFiles) => {
    const interrupted = failedUploads.filter(isInterruptedUpload);
    const { matches, unmatchedFiles, stillWaiting } = matchPickedFiles(interrupted, pickedFiles);
    const results = [];
    let next = 0;
    const worker = async () => {
      while (next < matches.length) {
        const { entry, file } = matches[next++];
        results.push(
          entry.resume?.kind === AI_ZIP_KIND
            ? await resendInterruptedZip(entry, file)
            : await resendFailedUpload(
                { ...entry, retryContext: retryContextFor(entry, file) },
                file,
                entry.fileName
              )
        );
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(RETRY_ALL_CONCURRENCY, matches.length) }, worker)
    );
    return {
      matched: matches.length,
      succeeded: results.filter((r) => r.success).length,
      failed: results.filter((r) => !r.success).length,
      notInterrupted: unmatchedFiles.length,
      stillWaiting: stillWaiting.length,
    };
  };

  const clearAllUploads = () => {
    setUploads([]);
    setFailedUploads([]);
    setShowNotification(false);
    // Clear upload info map as well
    setUploadInfoMap(new Map());
    // Clear abort controllers
    setUploadAbortControllers(new Map());
    completedUploadIdsRef.current.clear();
    cancelledUploadIdsRef.current.clear();
  };

  const markUploadCompleted = (uploadId) => {
    completedUploadIdsRef.current.add(uploadId);
  };

  const clearCompletedUploadIds = () => {
    completedUploadIdsRef.current.clear();
    cancelledUploadIdsRef.current.clear();
  };

  const isUploadCancelled = (uploadId) => cancelledUploadIdsRef.current.has(uploadId);

  // ---- Surviving a reload -------------------------------------------------
  // The tray used to be wiped on every reload, which is exactly when a user
  // needs it: after a crash in the middle of hundreds of claim files. It is
  // now saved to browser storage (per signed-in user, no file contents) and
  // rebuilt on the next load; see utils/uploadTrayStorage.js.
  const trayRef = useRef({ uploads, failedUploads });
  trayRef.current = { uploads, failedUploads };
  const everHeldEntriesRef = useRef(uploads.length > 0 || failedUploads.length > 0);
  if (uploads.length > 0 || failedUploads.length > 0) {
    everHeldEntriesRef.current = true;
  }
  const savedKeyRef = useRef(getTrayStorageKey());
  const saveTimerRef = useRef(null);

  const flushTray = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    // A tray that has never held anything must not erase a saved one it has
    // not loaded yet (e.g. mounted on the login screen before sign-in).
    if (!everHeldEntriesRef.current) return;
    const key = getTrayStorageKey() || savedKeyRef.current;
    if (!key) return;
    savedKeyRef.current = key;
    saveTray(trayRef.current, key);
  };

  useEffect(() => {
    if (saveTimerRef.current) return; // a save is queued; it reads the latest state
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      flushTray();
    }, TRAY_SAVE_THROTTLE_MS);
  }, [uploads, failedUploads]);

  useEffect(() => () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  }, []);

  // Write the tray the instant the page goes away (reload, close, tab hidden)
  // rather than waiting out the throttle.
  useEffect(() => {
    const flushWhenHidden = () => {
      if (globalThis.document?.visibilityState === 'hidden') flushTray();
    };
    globalThis.addEventListener('pagehide', flushTray);
    globalThis.addEventListener('beforeunload', flushTray);
    globalThis.document?.addEventListener?.('visibilitychange', flushWhenHidden);
    return () => {
      globalThis.removeEventListener('pagehide', flushTray);
      globalThis.removeEventListener('beforeunload', flushTray);
      globalThis.document?.removeEventListener?.('visibilitychange', flushWhenHidden);
    };
  }, []);

  // Tell the user before they walk away from files that are still sending.
  // Browsers show their own generic "Leave site?" box; the text is not ours.
  // A zip whose bytes all arrived (serverJobId) is being unpacked by the
  // server; leaving the page does not hurt it, so it does not warrant a warning.
  const hasUnfinishedUploads = uploads.some(
    (upload) => upload.status !== 'completed' && !upload.serverJobId
  );
  useEffect(() => {
    if (!hasUnfinishedUploads) return undefined;
    const warnBeforeLeaving = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    globalThis.addEventListener('beforeunload', warnBeforeLeaving);
    return () => globalThis.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [hasUnfinishedUploads]);

  // Signing in or out. Starting signed-out (as every reload does until the
  // session check returns) must NOT wipe the saved tray: only an actual
  // sign-out clears it.
  // Bumped on every real sign-out. Background work that started earlier (a
  // zip being checked on) compares against it and drops its result, so it
  // can never put a signed-out user's zip back on the tray or into the
  // next user's saved list.
  const signOutEpochRef = useRef(0);
  const wasAuthenticatedRef = useRef(isAuthenticated);
  const shownRestoredRef = useRef(false);
  useEffect(() => {
    const wasAuthenticated = wasAuthenticatedRef.current;
    wasAuthenticatedRef.current = isAuthenticated;

    if (wasAuthenticated && !isAuthenticated) {
      console.log('User logged out, clearing all uploads');
      signOutEpochRef.current += 1;
      clearAllUploads();
      clearSavedTray(savedKeyRef.current);
      return;
    }

    if (!isAuthenticated) return;

    // Signed in (on load, or after the login screen): bring back what a
    // previous page load left unfinished.
    const key = getTrayStorageKey();
    if (key) savedKeyRef.current = key;
    const stillEmpty = trayRef.current.uploads.length === 0 && trayRef.current.failedUploads.length === 0;
    if (stillEmpty && !restoredTrayRef.current) {
      const restored = loadTray();
      if (restored) {
        restoredTrayRef.current = restored;
        setUploads(restored.uploads);
        setFailedUploads(restored.failedUploads);
        shownRestoredRef.current = true;
        setShowNotification(true);
      }
    } else if (restoredTrayRef.current && !shownRestoredRef.current) {
      shownRestoredRef.current = true;
      setShowNotification(true);
    }
  }, [isAuthenticated]);


  // Session timeout listener to clear uploads when session expires
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Check if user_id was removed (session timeout)
      if (e.key === 'user_id' && e.newValue === null) {
        // Only clear if there are no active uploads in progress
        setUploads(currentUploads => {
          const uploadsInProgress = currentUploads.filter(upload => 
            upload.progress < 100 && upload.status !== 'completed'
          );
          
          if (uploadsInProgress.length === 0) {
            console.log('Session timeout detected, clearing all uploads');
            clearAllUploads();
          } else {
            console.log('Session timeout detected, but uploads in progress - keeping uploads');
          }
          
          return currentUploads;
        });
      }
    };

    // Function to check session status and clear uploads if needed
    // IMPORTANT: Only clear if there are NO active uploads in progress
    const checkSessionAndClearUploads = () => {
      // Use Redux authentication state as primary source of truth
      // Only check cookies/localStorage as fallback if Redux state is not available
      setUploads(currentUploads => {
        const uploadsInProgress = currentUploads.filter(upload => 
          upload.progress < 100 && upload.status !== 'completed'
        );
        
        // If there are uploads in progress, don't clear - session might be valid but check failed
        if (uploadsInProgress.length > 0) {
          return currentUploads; // Keep uploads if they're in progress
        }
        
        // Only check session if no uploads are in progress
        const userId = localStorage.getItem('user_id');
        let accessToken = false;
        
        try {
          // Try to check for access token in cookies
          accessToken = document.cookie.includes('access_token');
        } catch (error) {
          // If cookie access fails (e.g., HttpOnly cookies), assume token exists
          // This prevents false positives during large uploads
          console.warn('Could not check access token cookie:', error);
          accessToken = true; // Assume token exists to prevent false clearing
        }
        
        // Only clear if both userId and isAuthenticated are false
        // Prefer Redux state over cookie check for reliability
        if (!userId && !isAuthenticated) {
          console.log('No valid session found and no uploads in progress, clearing all uploads');
          clearAllUploads();
        }
        
        return currentUploads;
      });
    };

    // Listen for localStorage changes (session timeout from other tabs)
    globalThis.addEventListener('storage', handleStorageChange);
    
    // Check session status periodically (every 30 seconds)
    // But only clear if no uploads are in progress
    const sessionCheckInterval = setInterval(checkSessionAndClearUploads, 30000);

    // Check if user_id exists on mount, if not clear uploads (only if no uploads in progress)
    checkSessionAndClearUploads();

    return () => {
      globalThis.removeEventListener('storage', handleStorageChange);
      clearInterval(sessionCheckInterval);
    };
  }, [isAuthenticated]);

  const closeNotification = () => {
    setShowNotification(false);
  };

  const closeNotificationOnly = () => {
    // GlobalUploadNotification already checks for uploads in progress before calling this
    // This function is only called when all uploads are completed
    setShowNotification(false);
    setUploads([]);
    setFailedUploads([]);
    // Clear upload info map as well
    setUploadInfoMap(new Map());
    // Clear abort controllers
    setUploadAbortControllers(new Map());
    completedUploadIdsRef.current.clear();
    cancelledUploadIdsRef.current.clear();
  };

  const createCancelledUpload = (upload, uploadInfo) => {
    return {
      id: upload.id,
      fileName: uploadInfo?.fileName || upload.fileName || 'Unknown file',
      fileSize: uploadInfo?.fileSize || upload.fileSize || 0,
      status: 'cancelled',
      error: 'Upload cancelled by user',
      uploadIndex: upload.uploadIndex || uploadInfo?.uploadIndex,
      totalUploads: upload.totalUploads || uploadInfo?.totalUploads,
      batchId: uploadInfo?.batchId || upload.batchId,
      matterId: upload.matterId || uploadInfo?.matterId,
      matterLabel: upload.matterLabel || uploadInfo?.matterLabel
    };
  };

  const addCancelledUploadsToFailedList = (cancelledUploads, prevFailed) => {
    const existingIds = new Set(prevFailed.map(upload => upload.id));
    const newCancelledUploads = cancelledUploads.filter(upload => !existingIds.has(upload.id));
    return [...prevFailed, ...newCancelledUploads];
  };

  const processUploadsForCancellation = (uploads) => {
    const uploadsInProgress = uploads.filter(upload => 
      upload.progress < 100 && upload.status !== 'completed'
    );
    
    const completedUploads = uploads.filter(upload => 
      upload.progress === 100 && upload.status === 'completed'
    );
    
    // Uploads that completed before cancel
    const completedBeforeCancel = uploadsInProgress.filter(upload =>
      completedUploadIdsRef.current.has(upload.id)
    ).map(upload => ({ ...upload, progress: 100, status: 'completed' }));
    
    const cancelledUploads = uploadsInProgress
      .filter(upload => !completedUploadIdsRef.current.has(upload.id))
      .map(upload => {
        const uploadInfo = uploadInfoMap.get(upload.id);
        return createCancelledUpload(upload, uploadInfo);
      });
    
    return { cancelledUploads, completedUploads: [...completedUploads, ...completedBeforeCancel] };
  };

  const cancelAllUploads = () => {
    // Abort all ongoing API requests
    for (const abortController of uploadAbortControllers.values()) {
      if (abortController && typeof abortController.abort === 'function') {
        abortController.abort();
      }
    }
    setUploadAbortControllers(new Map());
    
    // Process uploads for cancellation
    setUploads(prev => {
      const { cancelledUploads, completedUploads } = processUploadsForCancellation(prev);
      
      // Track cancelled IDs so processFiles skips them when signed URL API returns
      cancelledUploads.forEach(u => cancelledUploadIdsRef.current.add(u.id));
      
      // Add cancelled uploads to failed list
      setFailedUploads(prevFailed => 
        addCancelledUploadsToFailedList(cancelledUploads, prevFailed)
      );
      
      // Return only completed uploads
      return completedUploads;
    });
    
    setShowNotification(true);
  };

  const value = useMemo(() => ({
    markUploadCompleted,
    clearCompletedUploadIds,
    isUploadCancelled,
    uploads,
    failedUploads,
    showNotification,
    addUpload,
    addUploadAbortController,
    updateUpload,
    removeUpload,
    cancelUpload,
    addFailedUpload,
    removeFailedUpload,
    retryUpload,
    retryAllFailedUploads,
    renameAndRetryUpload,
    resumeInterruptedUploads,
    clearAllUploads,
    closeNotification,
    closeNotificationOnly,
    cancelAllUploads,
    uploadInfoMap, // Expose for debugging
    // Optimization state
    uploadedDocIds,
    optimizedDocIds,
    isUploadComplete,
  }), [
    markUploadCompleted,
    clearCompletedUploadIds,
    isUploadCancelled,
    uploads,
    failedUploads,
    showNotification,
    addUpload,
    addUploadAbortController,
    updateUpload,
    removeUpload,
    cancelUpload,
    addFailedUpload,
    removeFailedUpload,
    retryUpload,
    retryAllFailedUploads,
    renameAndRetryUpload,
    resumeInterruptedUploads,
    clearAllUploads,
    closeNotification,
    closeNotificationOnly,
    cancelAllUploads,
    uploadInfoMap,
    uploadedDocIds,
    optimizedDocIds,
    isUploadComplete,
  ]);

  return (
    <UploadNotificationContext.Provider value={value}>
      {children}
    </UploadNotificationContext.Provider>
  );
};

UploadNotificationProvider.propTypes = {
  children: PropTypes.node.isRequired,
}; 